<template>
  <v-card class="py-4 px-8 rounded-lg mb-10" elevation="1">
    <v-card-title> Compliance and inspection summary </v-card-title>
    <v-card-text v-if="inspectionHistoryStore.isFetching || rucStore.isFetching">
      Loading fleet summary...
    </v-card-text>
    <v-card-text v-else>
      <div class="text-subtitle-2 text-grey-darken-3 mb-1">RUC status</div>
      <v-row no-gutters class="mb-4">
        <v-col
          cols="6"
          class="issue-stat--clickable"
          role="button"
          tabindex="0"
          @click="goToFilteredList('rucOverdue')"
          @keyup.enter="goToFilteredList('rucOverdue')"
          @keyup.space="goToFilteredList('rucOverdue')"
        >
          <v-row no-gutters align="center">
            <v-col cols="8">
              <div class="text-body-2 text-grey">Overdue</div>
            </v-col>
            <v-col cols="4">
              <div
                class="text-h5 font-weight-bold"
                :class="{
                  'text-error': rucOverdueCount !== 0,
                  'text-success': rucOverdueCount === 0,
                }"
              >
                {{ rucOverdueCount }}
              </div>
            </v-col>
          </v-row>
        </v-col>
        <v-col
          cols="6"
          class="issue-stat--clickable"
          role="button"
          tabindex="0"
          @click="goToFilteredList('rucComingDue')"
          @keyup.enter="goToFilteredList('rucComingDue')"
          @keyup.space="goToFilteredList('rucComingDue')"
        >
          <v-row no-gutters align="center">
            <v-col cols="8">
              <div class="text-body-2 text-grey">
                Due within {{ RUC_COMING_DUE_THRESHOLD_KM }} km
              </div>
            </v-col>
            <v-col cols="4">
              <div
                class="text-h5 font-weight-bold"
                :class="{
                  'text-warning': rucComingDueCount !== 0,
                  'text-success': rucComingDueCount === 0,
                }"
              >
                {{ rucComingDueCount }}
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <div class="text-subtitle-2 text-grey-darken-3 mb-1">WOF/COF</div>
      <v-row no-gutters class="mb-4">
        <v-col
          cols="6"
          class="issue-stat--clickable"
          role="button"
          tabindex="0"
          @click="goToFilteredList('wofCofOverdue')"
          @keyup.enter="goToFilteredList('wofCofOverdue')"
          @keyup.space="goToFilteredList('wofCofOverdue')"
        >
          <v-row no-gutters align="center">
            <v-col cols="8">
              <div class="text-body-2 text-grey">Overdue</div>
            </v-col>
            <v-col cols="4">
              <div
                class="text-h5 font-weight-bold"
                :class="{
                  'text-error': wofCofOverdueCount !== 0,
                  'text-success': wofCofOverdueCount === 0,
                }"
              >
                {{ wofCofOverdueCount }}
              </div>
            </v-col>
          </v-row>
        </v-col>
        <v-col
          cols="6"
          class="issue-stat--clickable"
          role="button"
          tabindex="0"
          @click="goToFilteredList('wofCofComingDue')"
          @keyup.enter="goToFilteredList('wofCofComingDue')"
          @keyup.space="goToFilteredList('wofCofComingDue')"
        >
          <v-row no-gutters align="center">
            <v-col cols="8">
              <div class="text-body-2 text-grey">Due within 30 days</div>
            </v-col>
            <v-col cols="4">
              <div
                class="text-h5 font-weight-bold"
                :class="{
                  'text-warning': comingUpCount !== 0,
                  'text-success': comingUpCount === 0,
                }"
              >
                {{ comingUpCount }}
              </div>
            </v-col>
          </v-row>
        </v-col>
        <v-col
          cols="6"
          class="issue-stat--clickable"
          role="button"
          tabindex="0"
          @click="goToFilteredList('wofCofFailedLastCheck')"
          @keyup.enter="goToFilteredList('wofCofFailedLastCheck')"
          @keyup.space="goToFilteredList('wofCofFailedLastCheck')"
        >
          <v-row no-gutters align="center">
            <v-col cols="8">
              <div class="text-body-2 text-grey">Failed last check</div>
            </v-col>
            <v-col cols="4">
              <div
                class="text-h5 font-weight-bold"
                :class="{
                  'text-error': failedLastCount !== 0,
                  'text-success': failedLastCount === 0,
                }"
              >
                {{ failedLastCount }}
              </div>
            </v-col>
          </v-row>
        </v-col>

        <v-col
          cols="6"
          class="issue-stat--clickable"
          role="button"
          tabindex="0"
          @click="goToFilteredList('wofCofAwaitingRecheck')"
          @keyup.enter="goToFilteredList('wofCofAwaitingRecheck')"
          @keyup.space="goToFilteredList('wofCofAwaitingRecheck')"
        >
          <v-row no-gutters align="center">
            <v-col cols="8">
              <div class="text-body-2 text-grey">Awaiting recheck</div>
            </v-col>
            <v-col cols="4">
              <div
                class="text-h5 font-weight-bold"
                :class="{
                  'text-warning': awaitingRecheckCount !== 0,
                  'text-success': awaitingRecheckCount === 0,
                }"
              >
                {{ awaitingRecheckCount }}
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <div class="text-subtitle-2 text-grey-darken-3 mb-1">Registration</div>

      <v-row no-gutters class="mb-4">
        <v-col
          cols="6"
          class="issue-stat--clickable"
          role="button"
          tabindex="0"
          @click="goToFilteredList('registrationOverdue')"
          @keyup.enter="goToFilteredList('registrationOverdue')"
          @keyup.space="goToFilteredList('registrationOverdue')"
        >
          <v-row no-gutters align="center">
            <v-col cols="8">
              <div class="text-body-2 text-grey">Overdue</div>
            </v-col>
            <v-col cols="4">
              <div
                class="text-h5 font-weight-bold"
                :class="{
                  'text-error': registrationOverdueCount !== 0,
                  'text-success': registrationOverdueCount === 0,
                }"
              >
                {{ registrationOverdueCount }}
              </div>
            </v-col>
          </v-row>
        </v-col>
        <v-col
          cols="6"
          class="issue-stat--clickable"
          role="button"
          tabindex="0"
          @click="goToFilteredList('registrationComingDue')"
          @keyup.enter="goToFilteredList('registrationComingDue')"
          @keyup.space="goToFilteredList('registrationComingDue')"
        >
          <v-row no-gutters align="center">
            <v-col cols="8">
              <div class="text-body-2 text-grey">Due within 30 days</div>
            </v-col>
            <v-col cols="4">
              <div
                class="text-h5 font-weight-bold"
                :class="{
                  'text-warning': registrationComingUpCount !== 0,
                  'text-success': registrationComingUpCount === 0,
                }"
              >
                {{ registrationComingUpCount }}
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <div class="text-subtitle-2 text-grey-darken-3 mb-1">Data completeness</div>

      <v-row no-gutters class="mb-4">
        <v-col
          cols="6"
          class="issue-stat--clickable"
          role="button"
          tabindex="0"
          @click="goToFilteredList('noComplianceRecord')"
          @keyup.enter="goToFilteredList('noComplianceRecord')"
          @keyup.space="goToFilteredList('noComplianceRecord')"
        >
          <v-row no-gutters align="center">
            <v-col cols="8">
              <div class="text-body-2 text-grey">
                No compliance record on file
              </div>
            </v-col>
            <v-col cols="4">
              <div
                class="text-h5 font-weight-bold"
                :class="{
                  'text-warning': noComplianceRecordCount !== 0,
                  'text-success': noComplianceRecordCount === 0,
                }"
              >
                {{ noComplianceRecordCount }}
              </div>
            </v-col>
          </v-row>
        </v-col>
        <v-col
          cols="6"
          class="issue-stat--clickable"
          role="button"
          tabindex="0"
          @click="goToFilteredList('hasComplianceRecord')"
          @keyup.enter="goToFilteredList('hasComplianceRecord')"
          @keyup.space="goToFilteredList('hasComplianceRecord')"
        >
          <v-row no-gutters align="center">
            <v-col cols="8">
              <div class="text-body-2 text-grey">Compliance record on file</div>
            </v-col>
            <v-col cols="4">
              <div
                class="text-h5 font-weight-bold"
                :class="{
                  'text-warning': complianceRecordCountColour === 'warning',
                  'text-error': complianceRecordCountColour === 'error',
                  'text-success': complianceRecordCountColour === 'success',
                }"
              >
                {{ complianceRecordCount }}
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script lang="ts" setup>
import { useInspectionHistoryStore } from "@/domains/inspectionHistory";
import { useRucStore } from "@/domains/ruc";
import { useVehicleStore } from "@/domains/vehicles";
import type { VehicleIssueFilter } from "@/domains/vehicles/vehicleComplianceStatus";
import {
  RUC_COMING_DUE_THRESHOLD_KM,
  type VehicleRucFilter,
} from "@/domains/vehicles/vehicleRucStatus";
import { useDashboardNavStore } from "@/stores/dashboardNavStore";
import { computed } from "vue";

/**
 * First pass: basic fleet-wide counts derived from `complianceStatus` and
 * `records`. "Failed on multiple successive checks" is deliberately left
 * out here — it needs history across a vehicle's inspection records rather
 * than a single snapshot row, and overlaps with another background system,
 * so it wants a discussion before implementing.
 *
 * Registration only gets an "Overdue" stat, not the full WOF/COF-shaped
 * row: `maintenance_compliance_status` has one shared `days_until_due` /
 * `last_inspection_result` pair for the whole row, not one per check type,
 * so "coming up"/"failed last" can't be attributed to registration
 * specifically without guessing.
 */

const DUE_SOON_THRESHOLD_DAYS = 30;

const inspectionHistoryStore = useInspectionHistoryStore();
const rucStore = useRucStore();
const vehicleStore = useVehicleStore();
const dashboardNavStore = useDashboardNavStore();

/** Navigates to the fleet vehicle list, pre-filtered to whichever stat was clicked. */
function goToFilteredList(issueFilter: VehicleIssueFilter | VehicleRucFilter) {
  dashboardNavStore.showVehicleList(issueFilter);
}

/** Whole days from now until the given ISO date string (negative if past). */
function daysUntil(dateString: string): number {
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.floor((new Date(dateString).getTime() - Date.now()) / msPerDay);
}

const rucOverdueCount = computed(
  () =>
    rucStore.statuses.filter((status) => status.km_remaining < 0).length,
);

const rucComingDueCount = computed(
  () =>
    rucStore.statuses.filter(
      (status) =>
        status.km_remaining >= 0 &&
        status.km_remaining <= RUC_COMING_DUE_THRESHOLD_KM,
    ).length,
);

const wofCofOverdueCount = computed(
  () =>
    inspectionHistoryStore.complianceStatus.filter(
      (status) =>
        (status.days_until_due ?? 0) < 0 ||
        status.wof_cof_status?.toUpperCase().includes("EXPIRED"),
    ).length,
);

const failedLastCount = computed(
  () =>
    inspectionHistoryStore.complianceStatus.filter((status) =>
      status.last_inspection_result?.toUpperCase().includes("FAIL"),
    ).length,
);

const comingUpCount = computed(
  () =>
    inspectionHistoryStore.complianceStatus.filter((status) => {
      const days = status.days_until_due;
      return days != null && days >= 0 && days <= DUE_SOON_THRESHOLD_DAYS;
    }).length,
);

const registrationOverdueCount = computed(
  () =>
    inspectionHistoryStore.complianceStatus.filter((status) =>
      status.registration_status?.toUpperCase().includes("EXPIRED"),
    ).length,
);

const registrationComingUpCount = computed(
  () =>
    vehicleStore.vehicles.filter((vehicle) => {
      if (!vehicle.registrationExpiry) return false;
      const days = daysUntil(vehicle.registrationExpiry);
      return days >= 0 && days <= DUE_SOON_THRESHOLD_DAYS;
    }).length,
);

/** Vehicles with no `maintenance_compliance_status` row at all. */
const noComplianceRecordCount = computed(() => {
  const vehicleIdsWithStatus = new Set(
    inspectionHistoryStore.complianceStatus.map((status) => status.vehicle_id),
  );
  return vehicleStore.vehicles.filter(
    (vehicle) =>
      !vehicle.externalId || !vehicleIdsWithStatus.has(vehicle.externalId),
  ).length;
});

const complianceRecordCount = computed(() => {
  const vehicleIdsWithStatus = new Set(
    inspectionHistoryStore.complianceStatus.map((status) => status.vehicle_id),
  );
  return vehicleStore.vehicles.filter(
    (vehicle) =>
      vehicle.externalId && vehicleIdsWithStatus.has(vehicle.externalId),
  ).length;
});

const errorThreshold = 0.5;

const complianceRecordCountColour = computed<'error' | 'success' | 'warning'>(() => {
  const vehicleIdsWithStatus = new Set(
    inspectionHistoryStore.complianceStatus.map((status) => status.vehicle_id),
  );
  const totalVehicles = vehicleStore.vehicles.length;
  if (totalVehicles === 0 || vehicleIdsWithStatus.size >= totalVehicles) {
    return 'success';
  }
  const missingRatio = 1 - vehicleIdsWithStatus.size / totalVehicles;
  if (missingRatio >= errorThreshold) return 'error';
  return 'warning'
});

/** Inspection records that failed initially and haven't been rechecked yet. */
const awaitingRecheckCount = computed(
  () =>
    inspectionHistoryStore.records.filter(
      (record) =>
        record.initial_result?.toUpperCase().includes("FAIL") &&
        !record.recheck_inspection_date,
    ).length,
);
</script>

<style scoped>
.issue-stat--clickable {
  cursor: pointer;
  border-radius: 4px;
}

.issue-stat--clickable:hover,
.issue-stat--clickable:focus-visible {
  background-color: rgba(var(--v-theme-on-surface), 0.05);
}
</style>
